import { useParams, Link } from 'react-router-dom';
import { comics } from '@/mocks/comics';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import ScrollToTop from '@/components/base/ScrollToTop';

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === Number(id));

  if (!comic) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center px-4">
          <div className="text-center">
            <div className="w-24 h-24 mx-auto flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-unknow-line text-4xl text-foreground-300"></i>
            </div>
            <h2 className="mt-6 text-xl font-semibold text-foreground-700">漫画未找到</h2>
            <p className="mt-2 text-sm text-foreground-400">您要查看的漫画不存在或已下架</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 mt-6 px-6 py-2.5 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-full text-sm font-medium hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedComics = comics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 4);

  const chapters = Array.from({ length: Math.min(comic.chapters, 20) }, (_, i) => ({
    number: comic.chapters - i,
    title: `第${comic.chapters - i}话`,
    date: `2026-${String(8 - Math.floor(i / 4)).padStart(2, '0')}-${String(15 - i).padStart(2, '0')}`,
  }));

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩漫画网免费在线观看`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: '2026-01-15',
    dateModified: comic.updatedAt,
    image: comic.cover,
    publisher: {
      '@type': 'Organization',
      name: '韩漫画网',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://hanmanhua.com/comic/${comic.id}`,
    },
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navbar />

      <main className="pt-20">
        <div className="py-4 md:py-6 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: `${comic.categoryName}漫画`, href: `/category/${comic.category}` },
                { label: comic.title },
              ]}
            />
          </div>
        </div>

        {/* Comic Info */}
        <section className="py-10 md:py-14 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8 md:gap-10">
              <div className="flex-shrink-0 w-full sm:w-[280px] mx-auto lg:mx-0">
                <div className="aspect-[3/4] rounded-lg overflow-hidden">
                  <img
                    src={comic.cover}
                    alt={`${comic.title}漫画封面`}
                    title={`${comic.title} - 韩漫画网免费在线观看`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-900">
                  {comic.title}
                </h1>
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <span className="px-3 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-700 whitespace-nowrap">
                    {comic.categoryName}
                  </span>
                  <span className={`px-3 py-1 text-xs font-medium rounded-full whitespace-nowrap ${
                    comic.status === '连载中'
                      ? 'bg-secondary-100 text-secondary-700'
                      : 'bg-foreground-100 text-foreground-600'
                  }`}>
                    {comic.status}
                  </span>
                  {comic.isHot && (
                    <span className="px-3 py-1 text-xs font-medium rounded-full bg-accent-100 text-accent-700 whitespace-nowrap">
                      热门
                    </span>
                  )}
                </div>

                <div className="mt-6 flex flex-wrap items-center gap-6 text-sm text-foreground-500">
                  <span className="flex items-center gap-1.5 whitespace-nowrap">
                    <i className="ri-user-line text-foreground-400"></i>
                    作者：{comic.author}
                  </span>
                  <span className="flex items-center gap-1.5 whitespace-nowrap">
                    <i className="ri-eye-line text-foreground-400"></i>
                    {(comic.views / 10000).toFixed(1)}万次观看
                  </span>
                  <span className="flex items-center gap-1.5 whitespace-nowrap">
                    <i className="ri-star-fill text-yellow-500"></i>
                    {comic.rating}分
                  </span>
                  <span className="flex items-center gap-1.5 whitespace-nowrap">
                    <i className="ri-file-list-3-line text-foreground-400"></i>
                    共{comic.chapters}话
                  </span>
                </div>

                <div className="mt-6 flex flex-wrap gap-1.5">
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1.5 text-[11px] rounded-md bg-background-100 text-foreground-600 whitespace-nowrap"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>

                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <Link
                    to={`/comic/${comic.id}/read/1`}
                    className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-full text-sm md:text-base font-semibold hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-play-circle-line text-lg"></i>
                    开始阅读第一话
                  </Link>
                  <Link
                    to={`/comic/${comic.id}/read/${comic.chapters}`}
                    className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-background-100 text-foreground-700 rounded-full text-sm md:text-base font-medium hover:bg-background-200 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    阅读最新话
                  </Link>
                </div>

                <div className="mt-8 p-5 bg-background-100 rounded-lg">
                  <h3 className="text-sm font-semibold text-foreground-800 mb-3">作品简介</h3>
                  <div
                    className="text-sm text-foreground-600 leading-relaxed space-y-2"
                    dangerouslySetInnerHTML={{ __html: comic.descriptionHtml }}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Chapter List */}
        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-8">
              章节列表
            </h2>
            <div className="bg-background-50 rounded-lg overflow-hidden">
              <div className="divide-y divide-background-200/70">
                {chapters.map((chapter) => (
                  <Link
                    key={chapter.number}
                    to={`/comic/${comic.id}/read/${chapter.number}`}
                    className="flex items-center justify-between p-4 hover:bg-background-100/50 transition-colors duration-150 cursor-pointer group"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-foreground-400 font-mono min-w-[3rem] whitespace-nowrap">
                        第{chapter.number}话
                      </span>
                      <span className="text-sm text-foreground-700 group-hover:text-primary-500 transition-colors duration-150 line-clamp-1">
                        {chapter.title}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-foreground-400">
                      <span className="hidden sm:inline whitespace-nowrap">{chapter.date}</span>
                      <i className="ri-arrow-right-s-line text-lg group-hover:text-primary-500 transition-colors duration-150"></i>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
            {comic.chapters > 20 && (
              <div className="mt-6 text-center">
                <button className="px-8 py-2.5 bg-background-50 text-foreground-600 rounded-full text-sm font-medium hover:bg-background-200 transition-all duration-200 whitespace-nowrap cursor-pointer">
                  查看更多章节（共{comic.chapters}话）
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Related Comics */}
        {relatedComics.length > 0 && (
          <section className="py-12 md:py-16 px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-8">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} comic={c} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}